import App from "../../app";
import {
	OverviewScoreChart, ReviewSectionScoreChart,
	ReviewTotalScoreChart,
	TestProgressChart
} from "../../lib/chart";
import { AttemptId, ItemId, ProductId, QuizId } from "../../model/datasource";
import { ContentItem, ItemType } from "../../model/datasource/item";
import { ReviewScore } from "../../model/datasource/score";
import { HeaderFrontend, ItemFrontend, PqItemFrontend } from "../frontend";
import { DisplayItemFrontend } from "../frontend/displayitem";
import { HeaderView, ItemView } from "../view";
import { MathJaxObject } from "mathjax-full/js/components/startup";
import { ChartView } from "../view/chart";
import OverviewSummaryTable from "../../lib/overviewsummary";
import {
	AnswerType,
	MultipleChoiceAnswer,
	MultipleCorrectAnswer
} from "../../lib/userdata";

export class TestHeaderView<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends HeaderView<P, A, Z, I> {
	constructor(frontend?: HeaderFrontend<I>) {
		super(frontend);
	}

	init(app: App<P, A, Z, I>): void {
		const bindTarget = app.view.getBindTarget();
		[
			this.frontend.getBindPause(),
			this.frontend.getBindFlag(),
			this.frontend.getBindRandomize(),
		].forEach(bind => bind.bind(bindTarget));
	}

	displayProgress(chart: TestProgressChart, complete: number, total: number): void {
		const element = this.frontend.getProgressChart();
		chart.display(element, complete, total);
	}

	displayTotalScores(chart: ReviewTotalScoreChart, total: ReviewScore): void {
		throw new Error('Method not implemented.');
	}

	displayItem(content: ContentItem<I>): void {
		this.frontend.displayItem(content);
	}

	displayTotalTime(time: number): void {
		this.frontend.displayTotalTime(time);
	}

	displayTimer(show: boolean): void {
		this.frontend.displayTimer(show);
	}

	displayNext(show: boolean): void {
		this.frontend.displayNext(show);
	}

	displayPrevious(show: boolean): void {
		this.frontend.displayPrevious(show);
	}

	displayFinish(show: boolean): void {
		this.frontend.displayFinish(show);
	}

	displayFlag(flagged: boolean | null): void {
		this.frontend.displayFlag(flagged);
	}

	displayCountdown(show: boolean): void {
		throw new Error("Method not implemented.");
	}

	displayCurrentTime(time: number): void {
		throw new Error("Method not implemented.");
	}

	displayCountdownTime(time: number): void {
		throw new Error("Method not implemented.");
	}

	displayRandomize(randomize: boolean): void {
		throw new Error("Method not implemented.");
	}

	displayPosition(index: number, length: number): void {
		this.frontend.displayPosition(index, length);
	}
}

export class TestItemView<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends ItemView<P, A, Z, I> {
	frontend: PqItemFrontend<I>;

	constructor(frontend?: ItemFrontend<I>) {
		super(frontend);
	}

	init(app: App<P, A, Z, I>): void {
		const bindTarget = app.view.getBindTarget();
		[
			this.frontend.getBindHashChange(),
			this.frontend.getBindNext(),
			this.frontend.getBindPrevious(),
			...(app.config.swipe ? [
				this.frontend.getBindSwipeLeft(),
				this.frontend.getBindSwipeRight(),
			]: []),
			this.frontend.getBindReload(),
			this.frontend.getBindFinish(),
			this.frontend.getBindToggleContext(),
			this.frontend.getBindContextShown(),
			this.frontend.getBindResume(),
			this.frontend.getBindHideTimer()
		].forEach(bind => bind.bind(bindTarget));
		this.swipe.init(
			this.frontend.getSwipeTarget(),
			(typeof app.config.swipe == 'boolean' ? undefined : app.config.swipe)
		);
		this.frontend.initSortable();
		this.rebindKeyboard(app, 'multiplechoice');
		this.frontend.bindJquery(app.getJquery(), '.collapse', 'show.bs.collapse');
		this.frontend.bindJquery(app.getJquery(), '.collapse', 'shown.bs.collapse');
		this.frontend.bindJquery(app.getJquery(), '.collapse', 'hide.bs.collapse');
		this.frontend.bindJquery(app.getJquery(), '.collapse', 'hidden.bs.collapse');
		this.frontend.bindJquery(app.getJquery(), '.modal', 'shown.bs.modal');
		this.frontend.bindJquery(app.getJquery(), '.modal', 'hidden.bs.modal');
	}

	rebindKeyboard(app: App<P, A, Z, I>, type: ItemType): void {
		const bindTarget = app.view.getBindTarget();
		this.frontend.bindKeyboard([
				this.frontend.getKeyBindNext(),
				this.frontend.getKeyBindPrevious(),
				this.frontend.getKeyBindToggleContext(),
				this.frontend.getKeyBindAnswer(0),
				this.frontend.getKeyBindAnswer(1),
				this.frontend.getKeyBindAnswer(2),
				this.frontend.getKeyBindAnswer(3),
				this.frontend.getKeyBindAnswer(4),
				this.frontend.getKeyBindAnswer(5),
				this.frontend.getKeyBindAnswer(6),
				this.frontend.getKeyBindAnswer(7),
				this.frontend.getKeyBindAnswer(8),
				this.frontend.getKeyBindAnswer(9),
				this.frontend.getKeyBindShortcutModal()
			],
			this.frontend.getAnswers(),
			type,
			bindTarget,
			() => app.model.settings.getKeyboardShortcuts()
		);
	}

	displayLoading(status: "done" | "loading" | "error"): void {
		this.frontend.displayLoading(status);
	}

	displayFinishing(status: "ready" | "loading" | "error"): void {
		this.frontend.displayFinishing(status);
	}

	displaySelectedAnswer(type: ItemType, answer: AnswerType): void {
		if (type === 'multiplechoice') {
			this.frontend.displaySelectedAnswerMultipleChoice(answer as MultipleChoiceAnswer);
		} else if (type === 'multiplecorrect') {
			this.frontend.displaySelectedAnswerMultipleCorrect(answer as MultipleCorrectAnswer);
		}
	}

	protected displayItemClass(content: ContentItem<I>): DisplayItemFrontend<I> {
		return this.frontend.displayItemClass(content);
	}

	toggleContexts(contexts: string[]): void {
		this.frontend.toggleContexts(contexts);
	}

	displayFontSize(fontSize: number): void {
		this.frontend.displayFontSize(fontSize);
	}

	displayResults(url: string): void {
		this.frontend.displayResults(url);
	}

	displayPauseModal(jQuery: JQueryStatic, show: boolean): void {
		this.frontend.displayPauseModal(jQuery, show ? 'show' : 'hide');
	}

	displayItemSelectedAnswer(content: ContentItem<I>, bindTarget: EventTarget, answer: AnswerType | undefined): void {
		this.displayItemClass(content).displayItemSelectedAnswer(content, bindTarget, answer);
	}

	displayItemSameContext(content: ContentItem<I>, sameContexts: ContentItem<I>["contexts"]): void {
		const ids = sameContexts.map(c=>c.id);
		this.frontend.displayContexts(content.contexts.reduce((r, c) => ({ ...r, [c.id]: !ids.includes(c.id) }), {}));
	}

	displayItemRendered(content: ContentItem<I>, mathjax: MathJaxObject): void {
		this.displayItemClass(content).displayItemRendered(content, mathjax);
	}

	displayItemAlerts(content: ContentItem<I>): void {
		this.displayItemClass(content).displayItemAlerts(content);
	}
}

export class TestChartView<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends ChartView<P, A, Z, I> {
	init(app: App<P, A, Z, I>): void { }

	getReviewTotalScoreChart(): ReviewTotalScoreChart {
		throw new Error("Method not implemented.");
	}

	getReviewSectionScoreChart(): ReviewSectionScoreChart {
		throw new Error("Method not implemented.");
	}

	getOverviewScoreChart<A, Z>(): OverviewScoreChart<A, Z> {
		throw new Error("Method not implemented.");
	}

	getTestProgressChart(): TestProgressChart {
		return this.frontend.getTestProgressChart();
	}

	getOverviewSummaryTable(jQuery: JQueryStatic): OverviewSummaryTable<P> {
		throw new Error("Method not implemented.");
	}
}
